import React, { useState } from "react";
import { Mail, Phone, Pencil, Loader2, Eye, CheckCircle2 } from "lucide-react";
import { api, errorCode, errorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EnrichButton } from "@/components/EnrichButton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

const META = { email: { Icon: Mail, field: "enrichment_email", ph: "name@company.com" }, phone: { Icon: Phone, field: "enrichment_phone", ph: "+33 6 00 00 00 00" } };

function EditContact({ cand, kind, value, onChanged, testid }) {
  const { t } = useI18n();
  const [v, setV] = useState(value || "");
  const [open, setOpen] = useState(false);
  const save = async () => {
    try { await api.put(`/candidates/${cand.id}/local-contacts`, { [kind]: v.trim() }); toast.success(t("Saved in your catalog")); setOpen(false); onChanged(); }
    catch (e) { toast.error(errorDetail(e)); }
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setV(value || ""); }}>
      <PopoverTrigger asChild><button className="p-0.5 rounded hover:bg-muted" title={kind === "email" ? t("Edit email") : t("Edit phone")} data-testid={`${testid}-edit`}><Pencil className="w-3 h-3" /></button></PopoverTrigger>
      <PopoverContent className="w-72 space-y-2" onClick={(e) => e.stopPropagation()} data-testid={`${testid}-edit-popover`}>
        <div className="text-xs font-semibold">{kind === "email" ? t("Email") : t("Phone")}</div>
        <Input value={v} onChange={(e) => setV(e.target.value)} placeholder={META[kind].ph} className="h-8" data-testid={`${testid}-edit-input`} />
        <p className="text-[11px] text-muted-foreground">{t("Replaces the current value for your organization; the previous one is kept in history.")}</p>
        <div className="flex justify-end"><Button size="sm" onClick={save} data-testid={`${testid}-edit-save`}>{t("Save")}</Button></div>
      </PopoverContent>
    </Popover>
  );
}

function Chip({ cand, kind, onChanged, testid }) {
  const { setUpgradeOpen } = useAuth();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const b = cand.contact_brief?.[kind];
  const { Icon } = META[kind];
  const reveal = async () => {
    setBusy(true);
    try { await api.post(`/candidates/${cand.id}/contacts/${b.contact_id}/reveal`); onChanged(); }
    catch (e) { if (errorCode(e) === "upgrade_required") setUpgradeOpen(true); else toast.error(errorDetail(e)); } finally { setBusy(false); }
  };
  return (
    <span className="flex items-center gap-1 min-w-0" onClick={(e) => e.stopPropagation()} data-testid={testid} data-state={b?.value ? "ok" : b ? "masked" : "missing"}>
      {b?.value ? <>
        <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
        <a href={kind === "email" ? `mailto:${b.value}` : `tel:${b.value}`} className="font-mono text-[11px] text-foreground truncate max-w-[180px] hover:underline" translate="no" data-testid={`${testid}-value`}>{b.value}</a>
      </> : b ? <button onClick={reveal} disabled={busy} className="flex items-center gap-1 text-primary hover:underline" data-testid={`${testid}-reveal`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}<Icon className="w-3 h-3" />{t("Show")}</button>
        : cand.profile_url ? <EnrichButton candidateId={cand.id} enrichment={cand[META[kind].field]} kind={kind} onDone={onChanged} testid={`${testid}-enrich`} />
        : <span className="flex items-center gap-1 text-muted-foreground/60"><Icon className="w-3 h-3" />—</span>}
      <EditContact cand={cand} kind={kind} value={b?.value} onChanged={onChanged} testid={testid} />
    </span>
  );
}

export function ContactChips({ cand, onChanged, testid }) {
  return (
    <>
      <Chip cand={cand} kind="email" onChanged={onChanged} testid={`${testid}-email`} />
      <Chip cand={cand} kind="phone" onChanged={onChanged} testid={`${testid}-phone`} />
    </>
  );
}
